import { groupColors } from "@/configs/map/groups";
import { indexedDBService } from "@/services/storage";
import { useDataflowStore } from "@/stores/dataflow";
import type {
  LayerConfig,
  UnifiedGroupState,
} from "@/stores/map/types/layerState";
import type { MapLayerActions, MapLayerState } from "@/types/stores/map";
import { PROCESSED_LAYER_PREFIX } from "@/utils/constants/map";
import { computeBrickLayerCounts } from "@/utils/data/brickLayerCounts";
import {
  groupValuesIntoChunks,
  shouldComputeChunks,
} from "@/utils/data/chunking";
import { extractUniqueValuesFromGeoJSON } from "@/utils/data/geography";
import { logger } from "@/utils/debug";
import { saveGroupsConfigToIndexedDB } from "@/utils/storage/groupsConfig";

export function createGroupActions(
  set: any,
  get: () => MapLayerState & MapLayerActions
) {
  return {
    getDatasetId: (workspaceId: string): string | null => {
      const dataflow = useDataflowStore.getState().dataflows[workspaceId];
      if (!dataflow?.flow_definition_json) return null;

      const inputTransformer = dataflow.flow_definition_json.find(
        (t) => t.type === "user_file" || t.type === "data_input"
      );

      if (!inputTransformer) return null;

      if (inputTransformer.type === "user_file") {
        return (inputTransformer as any).file_id || null;
      } else if (inputTransformer.type === "data_input") {
        return (inputTransformer as any).db_table_id || null;
      }

      return null;
    },

    loadGroupsConfig: async (workspaceId: string) => {
      const datasetId = get().getDatasetId(workspaceId);
      if (!datasetId) {
        logger.log(
          "[MapStore] Cannot load groups config: datasetId is null for workspace",
          workspaceId
        );
        return;
      }

      try {
        const config = await indexedDBService.getGroupsConfig(datasetId);
        if (config) {
          logger.log(
            "[MapStore] Loaded groups config for dataset",
            datasetId,
            config
          );
          set(
            (state: MapLayerState) => ({
              groups: {
                ...state.groups,
                [workspaceId]: {
                  groupColumnName: config.groupColumnName,
                  visibleGroups: config.visibleGroups,
                  groupConfigs: config.groupConfigs,
                  layerConfig: config.layerConfig,
                },
              },
            }),
            false,
            "map/loadGroupsConfig"
          );
        } else {
          logger.log(
            "[MapStore] No saved groups config found for dataset",
            datasetId
          );
        }
      } catch (error) {
        logger.error("[MapStore] Failed to load groups config:", error);
      }
    },

    toggleGroup: (workspaceId: string, groupValue: string) => {
      const state = get();
      const groupState = state.groups[workspaceId] || {
        groupColumnName: null,
        visibleGroups: [],
        groupConfigs: {},
        chunkConfigs: {},
        pointConfigs: {},
      };
      const currentGroups = new Set(groupState.visibleGroups);
      if (currentGroups.has(groupValue)) {
        currentGroups.delete(groupValue);
      } else {
        currentGroups.add(groupValue);
      }
      get().setVisibleGroups(workspaceId, Array.from(currentGroups));
    },

    setGroupColumnName: (
      workspaceId: string,
      columnName: string | null,
      uniqueValues?: string[]
    ) => {
      // When columnName is null, clear chunks in the same state update to ensure immediate layer recalculation
      const shouldClearChunks = columnName === null;

      set(
        (state: MapLayerState) => {
          const groupState = state.groups[workspaceId] || {
            groupColumnName: null,
            visibleGroups: [],
            groupConfigs: {},
            chunkConfigs: {},
            pointConfigs: {},
          };

          // When selecting a column, set visibleGroups to all uniqueValues if provided
          // This ensures all groups are enabled initially
          // When deselecting, clear visibleGroups
          // If uniqueValues not provided, try to extract from existing GeoJSON data
          let effectiveUniqueValues = uniqueValues;
          if (columnName && !effectiveUniqueValues) {
            const geoJSONLayerId = `${PROCESSED_LAYER_PREFIX}${workspaceId}`;
            const geoJSONLayer = state.geoJSONLayers[geoJSONLayerId];
            if (geoJSONLayer?.data?.features) {
              effectiveUniqueValues = extractUniqueValuesFromGeoJSON(
                geoJSONLayer.data
              );
            }
          }

          const newVisibleGroups =
            columnName === null
              ? []
              : effectiveUniqueValues && effectiveUniqueValues.length > 0
              ? [...effectiveUniqueValues] // Ensure all values are included
              : groupState.visibleGroups || [];

          // Create unified groups synchronously for non-chunked case
          // This ensures unified groups exist when the map reads the state
          let unifiedGroupsUpdate: Record<string, UnifiedGroupState> =
            state.unifiedGroups;

          if (shouldClearChunks) {
            unifiedGroupsUpdate = {
              ...state.unifiedGroups,
              [workspaceId]: {
                groupColumnName: null,
                groups: [],
                groupConfigs: {},
              },
            };
          } else if (
            columnName &&
            effectiveUniqueValues &&
            effectiveUniqueValues.length > 0
          ) {
            const sortedUniqueValues = [...effectiveUniqueValues].sort();
            const needsChunks = shouldComputeChunks(sortedUniqueValues.length);

            // Check if chunks already exist for this column
            const existingChunks = groupState?.chunks;
            const existingChunksMatch =
              existingChunks &&
              needsChunks &&
              groupState.groupColumnName === columnName &&
              existingChunks.length > 0;

            if (existingChunksMatch) {
              // Use existing chunks immediately to prevent flash of individual groups
              const chunkConfigs = groupState.chunkConfigs || {};
              const unifiedChunks: UnifiedGroupState["groups"] =
                existingChunks.map((chunk) => ({
                  id: chunk.value,
                  type: "chunk" as const,
                  label: chunk.label,
                  values: chunk.values,
                  color: chunk.color,
                  shape: chunkConfigs[chunk.value]?.shape,
                  displayName: chunk.displayName,
                  from: chunkConfigs[chunk.value]?.from,
                  to: chunkConfigs[chunk.value]?.to,
                }));

              // Build groupConfigs from chunks
              const initialChunkConfigs: Record<string, LayerConfig> = {
                ...(groupState?.groupConfigs || {}),
              };
              existingChunks.forEach((chunk) => {
                const existingConfig = initialChunkConfigs[chunk.value];
                if (!existingConfig) {
                  initialChunkConfigs[chunk.value] = {
                    color: chunk.color,
                    shape: chunkConfigs[chunk.value]?.shape || "circle",
                    displayName: chunk.displayName,
                  };
                } else {
                  initialChunkConfigs[chunk.value] = {
                    ...existingConfig,
                    color: existingConfig.color?.trim() || chunk.color,
                    shape:
                      existingConfig.shape ||
                      chunkConfigs[chunk.value]?.shape ||
                      "circle",
                    displayName:
                      existingConfig.displayName || chunk.displayName,
                  };
                }
              });

              unifiedGroupsUpdate = {
                ...state.unifiedGroups,
                [workspaceId]: {
                  groupColumnName: columnName,
                  groups: unifiedChunks,
                  groupConfigs: initialChunkConfigs,
                  layerConfig: groupState?.layerConfig,
                  visualizationMode: groupState?.visualizationMode,
                  visualizationLayerType: groupState?.visualizationLayerType,
                  categoryColorMap: groupState?.categoryColorMap,
                },
              };
            } else if (!needsChunks) {
              // Only create unified groups if not chunked (chunks will be created in computeChunks)
              const unifiedGroups: UnifiedGroupState["groups"] =
                sortedUniqueValues.map((value, index) => {
                  const config = groupState?.groupConfigs?.[value];
                  return {
                    id: value,
                    type: "group" as const,
                    label: value,
                    values: [value],
                    color:
                      config?.color || groupColors[index % groupColors.length],
                    shape: config?.shape,
                    name: config?.name,
                  };
                });

              // Ensure groupConfigs includes entries for all groups with their initial colors/shapes
              const initialGroupConfigs: Record<string, LayerConfig> = {
                ...(groupState?.groupConfigs || {}),
              };
              unifiedGroups.forEach((group) => {
                const existingConfig = initialGroupConfigs[group.id];
                if (!existingConfig) {
                  initialGroupConfigs[group.id] = {
                    color: group.color,
                    shape: group.shape,
                    name: group.displayName,
                  };
                } else {
                  initialGroupConfigs[group.id] = {
                    ...existingConfig,
                    color: existingConfig.color?.trim() || group.color,
                    shape: existingConfig.shape || group.shape,
                    name: existingConfig.name || group.displayName,
                  };
                }
              });

              unifiedGroupsUpdate = {
                ...state.unifiedGroups,
                [workspaceId]: {
                  groupColumnName: columnName,
                  groups: unifiedGroups,
                  groupConfigs: initialGroupConfigs,
                  layerConfig: groupState?.layerConfig,
                  visualizationMode: groupState?.visualizationMode,
                  visualizationLayerType: groupState?.visualizationLayerType,
                  categoryColorMap: groupState?.categoryColorMap,
                },
              };
            }
          }

          // Determine if we should clear chunks (when creating non-chunked unified groups)
          const shouldClearChunksForNonChunked =
            unifiedGroupsUpdate[workspaceId]?.groups &&
            unifiedGroupsUpdate[workspaceId].groups.length > 0 &&
            !unifiedGroupsUpdate[workspaceId].groups.some(
              (g) => g.type === "chunk"
            );

          // Update layerVisibility when column is selected/changed
          const updatedLayerVisibility: Record<string, boolean> = {
            ...state.layerVisibility,
          };

          // Clear visibility for all groups/chunks when deselecting column
          if (shouldClearChunks) {
            // Remove all group/chunk visibility for this workspace
            Object.keys(updatedLayerVisibility).forEach((layerId) => {
              if (
                (layerId.startsWith(`group:${workspaceId}:`) ||
                  layerId.startsWith(`chunk:${workspaceId}:`)) &&
                updatedLayerVisibility[layerId]
              ) {
                delete updatedLayerVisibility[layerId];
              }
            });
          } else if (columnName && newVisibleGroups.length > 0) {
            // Enable all groups initially when column is selected
            // Note: Chunks will be enabled when they're created in computeChunks
            newVisibleGroups.forEach((groupId) => {
              const unifiedId = `group:${workspaceId}:${groupId}`;
              updatedLayerVisibility[unifiedId] = true;
            });

            // Also ensure layerVisibility is updated for unified groups if they exist
            // This ensures the map reacts immediately when unified groups are created
            // This is critical for non-chunked groups and chunks to show up immediately
            if (unifiedGroupsUpdate[workspaceId]?.groups) {
              unifiedGroupsUpdate[workspaceId].groups.forEach((group) => {
                if (group.type === "group") {
                  const unifiedId = `group:${workspaceId}:${group.id}`;
                  updatedLayerVisibility[unifiedId] = true;
                } else if (group.type === "chunk") {
                  const unifiedId = `chunk:${workspaceId}:${group.id}`;
                  updatedLayerVisibility[unifiedId] = true;
                }
              });
            }
          }

          const newState = {
            groups: {
              ...state.groups,
              [workspaceId]: {
                ...groupState,
                groupColumnName: columnName,
                visibleGroups: newVisibleGroups,
                // Clear chunks when:
                // 1. Deselecting column (shouldClearChunks), OR
                // 2. Creating non-chunked unified groups (shouldClearChunksForNonChunked)
                ...(shouldClearChunks || shouldClearChunksForNonChunked
                  ? { chunks: undefined }
                  : {}),
                // Reset visualization mode to "points" when deselecting column
                ...(shouldClearChunks
                  ? {
                      visualizationMode: "points" as const,
                      visualizationLayerType: null,
                      categoryColorMap: undefined, // Clear color map when deselecting column
                    }
                  : {}),
              },
            },
            unifiedGroups: unifiedGroupsUpdate,
            layerVisibility: updatedLayerVisibility,
          };
          return newState;
        },
        false,
        "map/setGroupColumnName"
      );

      if (columnName) {
        // Initialize or update categoryColorMap when column is selected/changed
        // This ensures colors remain stable and are only assigned once per column
        const currentState = get();
        const currentGroupState = currentState.groups[workspaceId];

        // Reinitialize categoryColorMap if column changed or if it doesn't exist
        // Check if we need to rebuild by comparing the current column with the stored one
        const needsReinit =
          !currentGroupState?.categoryColorMap ||
          currentGroupState.groupColumnName !== columnName;

        if (needsReinit && uniqueValues && uniqueValues.length > 0) {
          const sortedUniqueValues = [...uniqueValues].sort();
          const categoryColorMap: Record<string, string> = {};
          const groupConfigs = currentGroupState?.groupConfigs || {};

          sortedUniqueValues.forEach((category, index) => {
            // Use groupConfigs color if available, otherwise use default from groupColors
            const configColor = groupConfigs[category]?.color;
            const defaultColor = groupColors[index % groupColors.length];
            categoryColorMap[category] =
              configColor && configColor.trim() !== ""
                ? configColor
                : defaultColor;
          });

          set(
            (state: MapLayerState) => {
              const groupState = state.groups[workspaceId];
              if (groupState) {
                return {
                  groups: {
                    ...state.groups,
                    [workspaceId]: {
                      ...groupState,
                      categoryColorMap,
                    },
                  },
                };
              }
              return state;
            },
            false,
            "map/initializeCategoryColorMap"
          );
        }

        if (uniqueValues && uniqueValues.length > 0) {
          const sortedUniqueValues = [...uniqueValues].sort();
          const needsChunks = shouldComputeChunks(sortedUniqueValues.length);
          if (needsChunks) {
            // Check if chunks already exist - if so, they were already used in the initial set() call above
            // If not, wait for updateGeoJSONLayerData to compute them from GeoJSON data
            // The unified groups will be created in computeChunks when GeoJSON data is available
          } else {
            // Unified groups should already be created in the initial set() call above
            // But if they don't exist (e.g., if uniqueValues wasn't provided initially),
            // create them here as a fallback
            const currentState = get();
            const existingUnifiedGroups =
              currentState.unifiedGroups[workspaceId];
            if (
              !existingUnifiedGroups ||
              existingUnifiedGroups.groups.length === 0
            ) {
              // Create unified groups for non-chunked case (fallback)
              const currentGroupState = currentState.groups[workspaceId];
              const unifiedGroups: UnifiedGroupState["groups"] =
                sortedUniqueValues.map((value, index) => {
                  const config = currentGroupState?.groupConfigs?.[value];
                  return {
                    id: value,
                    type: "group" as const,
                    label: value,
                    values: [value],
                    color:
                      config?.color || groupColors[index % groupColors.length],
                    shape: config?.shape,
                    name: config?.name,
                  };
                });

              // Ensure groupConfigs includes entries for all groups with their initial colors/shapes
              const initialGroupConfigs: Record<string, LayerConfig> = {
                ...(currentGroupState?.groupConfigs || {}),
              };
              unifiedGroups.forEach((group) => {
                const existingConfig = initialGroupConfigs[group.id];
                if (!existingConfig) {
                  initialGroupConfigs[group.id] = {
                    color: group.color,
                    shape: group.shape,
                    name: group.displayName,
                  };
                } else {
                  initialGroupConfigs[group.id] = {
                    ...existingConfig,
                    color: existingConfig.color?.trim() || group.color,
                    shape: existingConfig.shape || group.shape,
                    name: existingConfig.name || group.displayName,
                  };
                }
              });

              const unifiedGroupState: UnifiedGroupState = {
                groupColumnName: columnName,
                groups: unifiedGroups,
                groupConfigs: initialGroupConfigs,
                layerConfig: currentGroupState?.layerConfig,
                visualizationMode: currentGroupState?.visualizationMode,
                visualizationLayerType:
                  currentGroupState?.visualizationLayerType,
                categoryColorMap: currentGroupState?.categoryColorMap,
              };

              logger.log(
                "[setGroupColumnName] Creating unified groups (non-chunked fallback)",
                {
                  workspaceId,
                  columnName,
                  unifiedGroupsCount: unifiedGroups.length,
                  unifiedGroupsIds: unifiedGroups.map((g) => g.id).slice(0, 5),
                }
              );

              set(
                (state: MapLayerState) => {
                  const groupState = state.groups[workspaceId];
                  if (groupState) {
                    // Update layerVisibility to enable all groups
                    const updatedLayerVisibility: Record<string, boolean> = {
                      ...state.layerVisibility,
                    };
                    unifiedGroups.forEach((group) => {
                      const unifiedId = `group:${workspaceId}:${group.id}`;
                      updatedLayerVisibility[unifiedId] = true;
                    });

                    // Ensure visibleGroups matches the new unified groups
                    const newVisibleGroups = unifiedGroups.map(
                      (group) => group.id
                    );

                    logger.log(
                      "[setGroupColumnName] Setting state with unified groups (fallback)",
                      {
                        workspaceId,
                        newVisibleGroupsCount: newVisibleGroups.length,
                        layerVisibilityKeys: Object.keys(updatedLayerVisibility)
                          .filter((k) => k.startsWith(`group:${workspaceId}:`))
                          .slice(0, 5),
                      }
                    );

                    return {
                      groups: {
                        ...state.groups,
                        [workspaceId]: {
                          ...groupState,
                          chunks: undefined,
                          visibleGroups: newVisibleGroups,
                        },
                      },
                      unifiedGroups: {
                        ...state.unifiedGroups,
                        [workspaceId]: unifiedGroupState,
                      },
                      layerVisibility: updatedLayerVisibility,
                    };
                  }
                  return state;
                },
                false,
                "map/clearChunks"
              );
            }
          }
        } else {
          const state = get();
          const geoJSONLayerId = `${PROCESSED_LAYER_PREFIX}${workspaceId}`;
          const geoJSONLayer = state.geoJSONLayers[geoJSONLayerId];
          if (geoJSONLayer?.data?.features) {
            const extractedUniqueValues = extractUniqueValuesFromGeoJSON(
              geoJSONLayer.data
            );
            if (shouldComputeChunks(extractedUniqueValues.length)) {
              // Check if chunks already exist and match current values to avoid recalculation
              const currentGroupState = state.groups[workspaceId];
              const existingChunks = currentGroupState?.chunks;
              const sortedValues = [...extractedUniqueValues].sort();
              const newValuesKey = sortedValues.join(",");
              const existingValuesKey = existingChunks
                ? existingChunks
                    .flatMap((c) => c.values)
                    .sort()
                    .join(",")
                : null;

              // Only compute chunks if they don't exist or values have changed
              if (newValuesKey !== existingValuesKey) {
                get().computeChunks(workspaceId, extractedUniqueValues);
              }
            } else {
              set(
                (state: MapLayerState) => {
                  const groupState = state.groups[workspaceId];
                  if (groupState) {
                    return {
                      groups: {
                        ...state.groups,
                        [workspaceId]: {
                          ...groupState,
                          chunks: undefined,
                        },
                      },
                    };
                  }
                  return state;
                },
                false,
                "map/clearChunks"
              );
            }
          }
        }
      }
      // Note: chunks are now cleared in the main set() call above when columnName is null
      // No need for separate clearChunks call

      saveGroupsConfigToIndexedDB(
        workspaceId,
        get().getDatasetId,
        (id) => get().groups[id],
        "setGroupColumnName"
      );
    },

    setVisibleGroups: (workspaceId: string, groups: string[]) => {
      set(
        (state: MapLayerState) => {
          const newState = {
            groups: {
              ...state.groups,
              [workspaceId]: {
                ...(state.groups[workspaceId] || {
                  groupColumnName: null,
                  visibleGroups: [],
                  groupConfigs: {},
                }),
                visibleGroups: groups,
              },
            },
          };
          return newState;
        },
        false,
        "map/setVisibleGroups"
      );

      saveGroupsConfigToIndexedDB(
        workspaceId,
        get().getDatasetId,
        (id) => get().groups[id],
        "setVisibleGroups"
      );
    },

    setGroupConfig: (
      workspaceId: string,
      groupValue: string,
      config: {
        name?: string;
        color?: string;
        shape?: "circle" | "square" | "triangle" | "star" | "diamond";
      }
    ) => {
      set(
        (state: MapLayerState) => {
          const groupState = state.groups[workspaceId] || {
            groupColumnName: null,
            visibleGroups: [],
            groupConfigs: {},
          };
          const unifiedGroups = state.unifiedGroups[workspaceId];

          // Update legacy groupConfigs
          const updatedGroupConfigs = {
            ...groupState.groupConfigs,
            [groupValue]: {
              ...(groupState.groupConfigs[groupValue] || {}),
              ...config,
            },
          };

          // Update unified state if it exists
          const updatedUnifiedGroups = unifiedGroups
            ? {
                ...unifiedGroups,
                groupConfigs: {
                  ...unifiedGroups.groupConfigs,
                  [groupValue]: {
                    ...(unifiedGroups.groupConfigs[groupValue] || {}),
                    name: config.name,
                    color: config.color,
                    shape: config.shape,
                  },
                },
              }
            : undefined;

          return {
            groups: {
              ...state.groups,
              [workspaceId]: {
                ...groupState,
                groupConfigs: updatedGroupConfigs,
              },
            },
            ...(updatedUnifiedGroups
              ? {
                  unifiedGroups: {
                    ...state.unifiedGroups,
                    [workspaceId]: updatedUnifiedGroups,
                  },
                }
              : {}),
          };
        },
        false,
        "map/setGroupConfig"
      );

      saveGroupsConfigToIndexedDB(
        workspaceId,
        get().getDatasetId,
        (id) => get().groups[id],
        "setGroupConfig"
      );
    },

    getGroupConfig: (workspaceId: string, groupValue: string) => {
      const state = get();
      const groupState = state.groups[workspaceId];
      return groupState?.groupConfigs[groupValue];
    },

    updateGroupLayerFilter: (
      _workspaceId: string,
      _layerId?: string,
      _availableColumns?: string[]
    ) => {},

    computeChunks: (workspaceId: string, uniqueValues: string[]) => {
      const state = get();
      const groupState = state.groups[workspaceId] || {
        groupColumnName: null,
        visibleGroups: [],
        groupConfigs: {},
      };

      const existingChunks = groupState.chunks;
      const sortedValues = [...uniqueValues].sort();
      const newValuesKey = sortedValues.join(",");

      const existingValuesKey = existingChunks
        ? existingChunks
            .flatMap((c) => c.values)
            .sort()
            .join(",")
        : null;

      if (shouldComputeChunks(uniqueValues.length)) {
        if (newValuesKey === existingValuesKey && existingChunks) {
          return;
        }

        const processedGroups = groupValuesIntoChunks(sortedValues, {
          forceChunksThreshold: 100,
          targetChunks: 10,
          defaultChunkSize: 10,
          alwaysChunk: false,
        });

        const chunkConfigs = groupState.chunkConfigs || {};
        const chunks = processedGroups
          .filter((g) => g.type === "chunk")
          .map((chunk, index) => {
            const existingConfig = chunkConfigs[chunk.value];
            return {
              value: chunk.value,
              label: chunk.label,
              values: chunk.values,
              color:
                existingConfig?.color ||
                groupColors[index % groupColors.length],
              displayName: existingConfig?.displayName,
            };
          });

        const newChunksKey = chunks
          .map((c) => c.value)
          .sort()
          .join(",");
        const existingChunksKey = existingChunks
          ? existingChunks
              .map((c) => c.value)
              .sort()
              .join(",")
          : null;

        if (newChunksKey !== existingChunksKey) {
          // Update both legacy groups and unifiedGroups
          const unifiedGroupState: UnifiedGroupState = {
            groupColumnName: groupState.groupColumnName,
            groups: chunks.map((chunk) => ({
              id: chunk.value,
              type: "chunk" as const,
              label: chunk.label,
              values: chunk.values,
              color: chunk.color,
              shape: chunkConfigs[chunk.value]?.shape,
              displayName: chunk.displayName,
              from: chunkConfigs[chunk.value]?.from,
              to: chunkConfigs[chunk.value]?.to,
            })),
            groupConfigs: (() => {
              // Start with existing groupConfigs
              const configs: Record<string, LayerConfig> = {
                ...groupState.groupConfigs,
              };
              // Ensure all chunks have entries in groupConfigs with their initial colors/shapes
              // Merge existing configs with chunk colors/shapes to ensure all chunks have colors
              chunks.forEach((chunk) => {
                const existingConfig = configs[chunk.value];
                if (!existingConfig) {
                  // No existing config - create new one with chunk's color/shape
                  configs[chunk.value] = {
                    color: chunk.color,
                    shape: chunkConfigs[chunk.value]?.shape || "circle",
                    name: chunk.displayName,
                  };
                } else {
                  // Merge existing config with chunk's color/shape if missing
                  const existingColor = existingConfig.color?.trim();
                  configs[chunk.value] = {
                    ...existingConfig,
                    color:
                      existingColor && existingColor !== ""
                        ? existingColor
                        : chunk.color,
                    shape:
                      existingConfig.shape ||
                      chunkConfigs[chunk.value]?.shape ||
                      "circle",
                    name: existingConfig.name || chunk.displayName,
                  };
                }
              });
              return configs;
            })(),
            layerConfig: groupState.layerConfig,
            visualizationMode: groupState.visualizationMode,
            visualizationLayerType: groupState.visualizationLayerType,
            categoryColorMap: groupState.categoryColorMap,
          };

          set(
            (state: MapLayerState) => {
              // Ensure all chunk values are in visibleGroups
              const allChunkValues = new Set<string>();
              chunks.forEach((chunk) => {
                chunk.values.forEach((value) => allChunkValues.add(value));
              });

              const currentVisibleGroups = groupState.visibleGroups || [];
              const newVisibleGroups = [
                ...new Set([
                  ...currentVisibleGroups,
                  ...Array.from(allChunkValues),
                ]),
              ];

              // Update layerVisibility to enable all chunks
              const updatedLayerVisibility: Record<string, boolean> = {
                ...state.layerVisibility,
              };
              unifiedGroupState.groups.forEach((chunk) => {
                const unifiedId = `chunk:${workspaceId}:${chunk.id}`;
                updatedLayerVisibility[unifiedId] = true;
              });

              return {
                groups: {
                  ...state.groups,
                  [workspaceId]: {
                    ...groupState,
                    chunks,
                    visibleGroups: newVisibleGroups,
                  },
                },
                unifiedGroups: {
                  ...state.unifiedGroups,
                  [workspaceId]: unifiedGroupState,
                },
                layerVisibility: updatedLayerVisibility,
              };
            },
            false,
            "map/computeChunks"
          );
        }
      } else {
        if (existingChunks) {
          set(
            (state: MapLayerState) => ({
              groups: {
                ...state.groups,
                [workspaceId]: {
                  ...groupState,
                  chunks: undefined,
                },
              },
            }),
            false,
            "map/computeChunks"
          );
        }
      }
    },

    getChunks: (workspaceId: string) => {
      const state = get();
      return state.groups[workspaceId]?.chunks;
    },

    setChunkConfig: (
      workspaceId: string,
      chunkValue: string,
      config: {
        displayName?: string;
        color?: string;
        shape?: "circle" | "square" | "triangle" | "star" | "diamond";
        from?: string;
        to?: string;
      }
    ) => {
      // Update both chunkConfigs and chunks array atomically in a single set call
      // This ensures proper reactivity and prevents race conditions
      set(
        (state: MapLayerState) => {
          const groupState = state.groups[workspaceId] || {
            groupColumnName: null,
            visibleGroups: [],
            groupConfigs: {},
            chunkConfigs: {},
            pointConfigs: {},
          };

          // Update chunkConfigs
          const updatedChunkConfigs = {
            ...(groupState.chunkConfigs || {}),
            [chunkValue]: {
              ...(groupState.chunkConfigs?.[chunkValue] || {}),
              ...config,
            },
          };

          // Update chunks array if it exists
          let updatedChunks = groupState.chunks;
          if (groupState.chunks) {
            const chunkIndex = groupState.chunks.findIndex(
              (c) => c.value === chunkValue
            );
            if (chunkIndex !== -1) {
              updatedChunks = [...groupState.chunks];
              if (updatedChunks[chunkIndex]) {
                // Update label if from/to changed
                let label = updatedChunks[chunkIndex].label;
                if (config.from && config.to) {
                  label =
                    config.from === config.to
                      ? config.from
                      : `${config.from}-${config.to}`;
                }

                updatedChunks[chunkIndex] = {
                  ...updatedChunks[chunkIndex],
                  displayName:
                    config.displayName !== undefined
                      ? config.displayName
                      : updatedChunks[chunkIndex].displayName,
                  label,
                  // Always update color if provided, otherwise keep existing
                  color:
                    config.color !== undefined
                      ? config.color
                      : updatedChunks[chunkIndex].color,
                };
              }
            }
          }

          // Update unified state if it exists
          const unifiedGroups = state.unifiedGroups[workspaceId];
          const updatedUnifiedGroups = unifiedGroups
            ? {
                ...unifiedGroups,
                groupConfigs: {
                  ...unifiedGroups.groupConfigs,
                  [chunkValue]: {
                    ...(unifiedGroups.groupConfigs[chunkValue] || {}),
                    name: config.displayName,
                    color: config.color,
                    shape: config.shape,
                  },
                },
                groups: unifiedGroups.groups.map((group) => {
                  if (group.id === chunkValue && group.type === "chunk") {
                    return {
                      ...group,
                      color:
                        config.color !== undefined ? config.color : group.color,
                      shape:
                        config.shape !== undefined ? config.shape : group.shape,
                      displayName:
                        config.displayName !== undefined
                          ? config.displayName
                          : group.displayName,
                      from:
                        config.from !== undefined ? config.from : group.from,
                      to: config.to !== undefined ? config.to : group.to,
                    };
                  }
                  return group;
                }),
              }
            : undefined;

          return {
            groups: {
              ...state.groups,
              [workspaceId]: {
                ...groupState,
                chunkConfigs: updatedChunkConfigs,
                ...(updatedChunks ? { chunks: updatedChunks } : {}),
              },
            },
            ...(updatedUnifiedGroups
              ? {
                  unifiedGroups: {
                    ...state.unifiedGroups,
                    [workspaceId]: updatedUnifiedGroups,
                  },
                }
              : {}),
          };
        },
        false,
        "map/setChunkConfig"
      );

      saveGroupsConfigToIndexedDB(
        workspaceId,
        get().getDatasetId,
        (id) => get().groups[id],
        "setChunkConfig"
      );
    },

    updateChunkAndPoints: (
      workspaceId: string,
      chunkValue: string,
      config: {
        displayName?: string;
        color?: string;
        shape?: "circle" | "square" | "triangle" | "star" | "diamond";
        from?: string;
        to?: string;
      }
    ) => {
      // Only update the chunk config - no individual point colors
      // All points in a cluster use the cluster color
      get().setChunkConfig(workspaceId, chunkValue, config);
    },

    getChunkConfig: (workspaceId: string, chunkValue: string) => {
      const state = get();
      const groupState = state.groups[workspaceId];
      return groupState?.chunkConfigs?.[chunkValue];
    },

    setPointConfig: (
      workspaceId: string,
      pointId: string,
      config: {
        color?: string;
        shape?: "circle" | "square" | "triangle" | "star" | "diamond";
        isVisible?: boolean;
      }
    ) => {
      set(
        (state: MapLayerState) => {
          const groupState = state.groups[workspaceId] || {
            groupColumnName: null,
            visibleGroups: [],
            groupConfigs: {},
            chunkConfigs: {},
            pointConfigs: {},
          };
          const newState = {
            groups: {
              ...state.groups,
              [workspaceId]: {
                ...groupState,
                pointConfigs: {
                  ...(groupState.pointConfigs || {}),
                  [pointId]: {
                    ...(groupState.pointConfigs?.[pointId] || {}),
                    ...config,
                  },
                },
              },
            },
          };
          return newState;
        },
        false,
        "map/setPointConfig"
      );

      saveGroupsConfigToIndexedDB(
        workspaceId,
        get().getDatasetId,
        (id) => get().groups[id],
        "setPointConfig"
      );
    },

    getPointConfig: (workspaceId: string, pointId: string) => {
      const state = get();
      const groupState = state.groups[workspaceId];
      return groupState?.pointConfigs?.[pointId];
    },

    // Layer config management (for layers with no groups/clusters)
    setLayerConfig: (
      workspaceId: string,
      config: {
        color?: string;
        shape?: "circle" | "square" | "triangle" | "star" | "diamond";
      }
    ) => {
      set(
        (state: MapLayerState) => {
          const groupState = state.groups[workspaceId] || {
            groupColumnName: null,
            visibleGroups: [],
            groupConfigs: {},
            chunkConfigs: {},
            pointConfigs: {},
          };
          const unifiedGroups = state.unifiedGroups[workspaceId];

          // Update unified state if it exists
          const updatedUnifiedGroups = unifiedGroups
            ? {
                ...unifiedGroups,
                layerConfig: {
                  ...(unifiedGroups.layerConfig || {}),
                  color: config.color,
                  shape: config.shape,
                },
              }
            : undefined;

          return {
            groups: {
              ...state.groups,
              [workspaceId]: {
                ...groupState,
                layerConfig: {
                  ...(groupState.layerConfig || {}),
                  ...config,
                },
              },
            },
            ...(updatedUnifiedGroups
              ? {
                  unifiedGroups: {
                    ...state.unifiedGroups,
                    [workspaceId]: updatedUnifiedGroups,
                  },
                }
              : {}),
          };
        },
        false,
        "map/setLayerConfig"
      );

      saveGroupsConfigToIndexedDB(
        workspaceId,
        get().getDatasetId,
        (id) => get().groups[id],
        "setLayerConfig"
      );
    },

    getLayerConfig: (workspaceId: string) => {
      const state = get();
      const groupState = state.groups[workspaceId];
      return groupState?.layerConfig;
    },

    // Visualization mode management
    setVisualizationMode: (
      workspaceId: string,
      mode: "points" | "layers",
      layerType?: "municipalities" | "countries" | "timezones" | null
    ) => {
      const state = get();
      const groupState = state.groups[workspaceId] || {
        groupColumnName: null,
        visibleGroups: [],
        groupConfigs: {},
        chunkConfigs: {},
        pointConfigs: {},
      };

      // If switching to brick layers, create individual groups for each region
      if (mode === "layers" && layerType) {
        // Find the GeoJSON layer data for this workspace
        const geoJSONLayerEntry = Object.entries(state.geoJSONLayers).find(
          ([_layerId, layerData]) => layerData?.data
        );
        const pointData = geoJSONLayerEntry?.[1]?.data;
        const layerGeoJSON = state.staticData[layerType];

        if (pointData && layerGeoJSON) {
          // Compute individual region data with point counts (async to avoid blocking UI)
          computeBrickLayerCounts(pointData, layerGeoJSON, layerType)
            .then((regions) => {
              // Filter out regions with 0 points and sort by point count (descending) then by display name
              const regionsWithPoints = regions
                .filter((region) => region.pointCount > 0)
                .sort((a, b) => {
                  // First sort by point count (descending)
                  if (b.pointCount !== a.pointCount) {
                    return b.pointCount - a.pointCount;
                  }
                  // Then sort by display name
                  const nameA = a.displayName || a.regionId;
                  const nameB = b.displayName || b.regionId;
                  return nameA.localeCompare(nameB);
                });

              // Create individual groups for each region
              const unifiedGroups: UnifiedGroupState["groups"] =
                regionsWithPoints.map((region, index) => {
                  const regionId = region.regionId;
                  const pointCount = region.pointCount;

                  // Use existing layerRegionConfig if available, otherwise create new
                  const existingRegionConfig =
                    groupState?.layerRegionConfigs?.[regionId];
                  const existingGroupConfig =
                    groupState?.groupConfigs?.[regionId];

                  // Build label: display name (if available) + point count, or just point count
                  const baseLabel = region.displayName || regionId;
                  const label = `${baseLabel} (${pointCount})`;

                  // Assign color: use existing config color, or assign based on point count
                  // Colors should match what's shown on the map
                  let color: string;
                  if (existingRegionConfig?.color) {
                    color = existingRegionConfig.color;
                  } else if (existingGroupConfig?.color) {
                    color = existingGroupConfig.color;
                  } else {
                    // Assign color based on point count (use index for consistent coloring)
                    // This ensures regions with same count get same color
                    color = groupColors[index % groupColors.length];
                  }

                  return {
                    id: regionId,
                    type: "group" as const,
                    label,
                    values: [regionId],
                    color,
                    shape: existingGroupConfig?.shape,
                    displayName:
                      existingRegionConfig?.displayName ||
                      existingGroupConfig?.name ||
                      label,
                  };
                });

              // Create layerRegionConfigs for each region (used by map for coloring)
              const initialLayerRegionConfigs: Record<
                string,
                { displayName?: string; color?: string; isVisible?: boolean }
              > = {
                ...(groupState?.layerRegionConfigs || {}),
              };

              // Create groupConfigs for each region (used by sidebar)
              const initialGroupConfigs: Record<string, LayerConfig> = {
                ...(groupState?.groupConfigs || {}),
              };

              unifiedGroups.forEach((group) => {
                const regionId = group.id;
                const region = regionsWithPoints.find(
                  (r) => r.regionId === regionId
                );

                if (region) {
                  // Update layerRegionConfigs
                  const existingRegionConfig =
                    initialLayerRegionConfigs[regionId];
                  if (!existingRegionConfig) {
                    initialLayerRegionConfigs[regionId] = {
                      displayName: region.displayName || group.displayName,
                      color: group.color,
                      isVisible: true,
                    };
                  } else {
                    // Preserve existing config but update color if not set
                    initialLayerRegionConfigs[regionId] = {
                      ...existingRegionConfig,
                      color: existingRegionConfig.color || group.color,
                      displayName:
                        existingRegionConfig.displayName ||
                        region.displayName ||
                        group.displayName,
                    };
                  }

                  // Update groupConfigs
                  const existingGroupConfig = initialGroupConfigs[regionId];
                  if (!existingGroupConfig) {
                    initialGroupConfigs[regionId] = {
                      color: group.color,
                      shape: group.shape,
                      name: group.displayName,
                    };
                  } else {
                    initialGroupConfigs[regionId] = {
                      ...existingGroupConfig,
                      color: existingGroupConfig.color?.trim() || group.color,
                      shape: existingGroupConfig.shape || group.shape,
                      name: existingGroupConfig.name || group.displayName,
                    };
                  }
                }
              });

              // Update visible groups to include all regions
              const newVisibleGroups = unifiedGroups.map((group) => group.id);

              // Update layer visibility for all regions
              const updatedLayerVisibility: Record<string, boolean> = {
                ...state.layerVisibility,
              };
              unifiedGroups.forEach((group) => {
                const unifiedId = `group:${workspaceId}:${group.id}`;
                updatedLayerVisibility[unifiedId] = true;
              });

              set(
                (state: MapLayerState) => ({
                  groups: {
                    ...state.groups,
                    [workspaceId]: {
                      ...groupState,
                      visualizationMode: mode,
                      visualizationLayerType: layerType ?? null,
                      visibleGroups: newVisibleGroups,
                      layerRegionConfigs: initialLayerRegionConfigs,
                    },
                  },
                  unifiedGroups: {
                    ...state.unifiedGroups,
                    [workspaceId]: {
                      groupColumnName: null, // No column grouping for brick layers
                      groups: unifiedGroups,
                      groupConfigs: initialGroupConfigs,
                      layerConfig: groupState?.layerConfig,
                      visualizationMode: mode,
                      visualizationLayerType: layerType ?? null,
                      categoryColorMap: groupState?.categoryColorMap,
                    },
                  },
                  layerVisibility: updatedLayerVisibility,
                }),
                false,
                "map/setVisualizationMode-brickLayers"
              );

              saveGroupsConfigToIndexedDB(
                workspaceId,
                get().getDatasetId,
                (id) => get().groups[id],
                "setVisualizationMode"
              );
            })
            .catch((error) => {
              logger.error(
                "[MapStore] Failed to compute brick layer counts:",
                error
              );
            });
          return;
        }
      }

      // For points mode or if data is not available, just update the mode
      set(
        (state: MapLayerState) => {
          return {
            groups: {
              ...state.groups,
              [workspaceId]: {
                ...groupState,
                visualizationMode: mode,
                visualizationLayerType: layerType ?? null,
              },
            },
          };
        },
        false,
        "map/setVisualizationMode"
      );

      saveGroupsConfigToIndexedDB(
        workspaceId,
        get().getDatasetId,
        (id) => get().groups[id],
        "setVisualizationMode"
      );
    },

    getVisualizationMode: (workspaceId: string) => {
      const state = get();
      const groupState = state.groups[workspaceId];
      if (!groupState) {
        return undefined;
      }
      return {
        mode: groupState.visualizationMode || "points",
        layerType: groupState.visualizationLayerType || null,
      };
    },

    // Layer region config management
    setLayerRegionConfig: (
      workspaceId: string,
      regionId: string,
      config: { displayName?: string; color?: string; isVisible?: boolean }
    ) => {
      set(
        (state: MapLayerState) => {
          const groupState = state.groups[workspaceId] || {
            groupColumnName: null,
            visibleGroups: [],
            groupConfigs: {},
            chunkConfigs: {},
            pointConfigs: {},
            layerRegionConfigs: {},
          };

          // Update layerRegionConfigs
          const updatedLayerRegionConfigs = {
            ...(groupState.layerRegionConfigs || {}),
            [regionId]: {
              ...(groupState.layerRegionConfigs?.[regionId] || {}),
              ...config,
            },
          };

          // Update groupConfigs to match (for sidebar display)
          // Use config values if provided, otherwise preserve existing values
          const existingGroupConfig = groupState.groupConfigs?.[regionId] || {};
          const updatedGroupConfigs = {
            ...(groupState.groupConfigs || {}),
            [regionId]: {
              ...existingGroupConfig,
              name:
                config.displayName !== undefined
                  ? config.displayName
                  : existingGroupConfig.name,
              color:
                config.color !== undefined
                  ? config.color
                  : existingGroupConfig.color,
              shape: existingGroupConfig.shape, // Preserve shape (regions don't have shapes)
            },
          };

          // Update unifiedGroups if it exists (for sidebar and map)
          const unifiedGroupState = state.unifiedGroups[workspaceId];
          let updatedUnifiedGroups = unifiedGroupState;

          if (unifiedGroupState) {
            // Update unifiedGroups.groupConfigs
            const existingUnifiedGroupConfig =
              unifiedGroupState.groupConfigs?.[regionId] || {};
            const updatedUnifiedGroupConfigs = {
              ...(unifiedGroupState.groupConfigs || {}),
              [regionId]: {
                ...existingUnifiedGroupConfig,
                name:
                  config.displayName !== undefined
                    ? config.displayName
                    : existingUnifiedGroupConfig.name,
                color:
                  config.color !== undefined
                    ? config.color
                    : existingUnifiedGroupConfig.color,
                shape: existingUnifiedGroupConfig.shape, // Preserve shape (regions don't have shapes)
              },
            };

            // Update unifiedGroups.groups to update the color in the group object itself
            // Preserve the point count in the label when updating displayName
            const updatedUnifiedGroupsList = unifiedGroupState.groups.map(
              (group) => {
                if (group.id === regionId) {
                  // Extract point count from existing label (format: "Name (count)")
                  let pointCount: number | null = null;
                  const labelMatch = group.label?.match(/\((\d+)\)$/);
                  if (labelMatch) {
                    pointCount = parseInt(labelMatch[1], 10);
                  }

                  // Build new label with updated displayName and preserved point count
                  let newLabel = group.label;
                  if (config.displayName !== undefined) {
                    const baseLabel = config.displayName;
                    if (pointCount !== null && !isNaN(pointCount)) {
                      newLabel = `${baseLabel} (${pointCount})`;
                    } else {
                      newLabel = baseLabel;
                    }
                  }

                  return {
                    ...group,
                    color:
                      config.color !== undefined ? config.color : group.color,
                    displayName:
                      config.displayName !== undefined
                        ? config.displayName
                        : group.displayName,
                    label: newLabel, // Update label to preserve point count
                  };
                }
                return group;
              }
            );

            updatedUnifiedGroups = {
              ...unifiedGroupState,
              groups: updatedUnifiedGroupsList,
              groupConfigs: updatedUnifiedGroupConfigs,
            };
          }

          return {
            groups: {
              ...state.groups,
              [workspaceId]: {
                ...groupState,
                layerRegionConfigs: updatedLayerRegionConfigs,
                groupConfigs: updatedGroupConfigs,
              },
            },
            unifiedGroups: updatedUnifiedGroups
              ? {
                  ...state.unifiedGroups,
                  [workspaceId]: updatedUnifiedGroups,
                }
              : state.unifiedGroups,
          };
        },
        false,
        "map/setLayerRegionConfig"
      );

      saveGroupsConfigToIndexedDB(
        workspaceId,
        get().getDatasetId,
        (id) => get().groups[id],
        "setLayerRegionConfig"
      );
    },

    getLayerRegionConfig: (workspaceId: string, regionId: string) => {
      const state = get();
      const groupState = state.groups[workspaceId];
      return groupState?.layerRegionConfigs?.[regionId];
    },
  };
}
